import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowLeft,
  ArrowRight,
  Building2,
  Dumbbell,
  Home,
  LandPlot,
  PartyPopper,
  Sparkles,
  Trees,
  Trophy,
  WavesLadder,
} from "lucide-react";

import aerialView from "../../assets/villas-images/Aerial-View.jpg";
import banquetHall from "../../assets/villas-images/Banquet-Hall.jpg";
import basketballCourt from "../../assets/villas-images/Basketball-Court.jpg";
import clubHouse from "../../assets/villas-images/Club-House.jpg";
import gymArea from "../../assets/villas-images/Gym-Area.jpg";
import interiorView from "../../assets/villas-images/Interiorview.jpg";
import playArea from "../../assets/villas-images/Play-Area.jpg";
import streetView from "../../assets/villas-images/streetview.jpg";
import tennisGround from "../../assets/villas-images/Tennis-Ground.jpg";

const galleryItems = [
  {
    title: "Street View",
    icon: Trees,
    image: streetView,
    description:
      "The comforts of home aren't limited to the four walls of your plush villas. Feel at home at the well-maintained community, designed specifically to facilitate interactions and bring you closer to nature",
  },
  {
    title: "Club House/Pool View",
    icon: WavesLadder,
    image: clubHouse,
    description:
      "Enjoy an evening with family and friends close to home or simply enjoy a vibrant, lively community any time of the day - the club house has something for everyone",
  },
  {
    title: "Banquet Hall",
    icon: PartyPopper,
    image: banquetHall,
    description:
      "After all, celebrations are best when they are close to home. With ample space and on-demand staff, we have made sure that every event gets a touch of class on our premises",
  },
  {
    title: "Gym Area",
    icon: Dumbbell,
    image: gymArea,
    description:
      "The dreaded \"G\" word may not be the reason you bought your home. However, absolute privacy and control of your schedule may be good enough reasons to finally hitting those new year resolutions",
  },
  {
    title: "Play Area",
    icon: Sparkles,
    image: playArea,
    description:
      "Engaging spaces with some of the cutest make-believe forts and ponies we can find, we can assure you that your children (and their friends) will use their imagination to the fullest while getting that dose of fresh air on the side",
  },
  {
    title: "Tennis Ground",
    icon: Trophy,
    image: tennisGround,
    description:
      "A tennis court is good, but a synthetic court with floodlights where you can play 24x7? Even better",
  },
  {
    title: "Basketball Court",
    icon: LandPlot,
    image: basketballCourt,
    description:
      "As a resident, enjoy round-the-clock access to our basketball court",
  },
  {
    title: "Aerial View",
    icon: Building2,
    image: aerialView,
    description:
      "Our impeccably built homes and fully secure neighbourhood are designed to inspire interaction by giving you a sense of calm and the security you need to truly feel at home",
  },
  {
    title: "Interior View",
    icon: Home,
    image: interiorView,
    description:
      "A busy day's retreat, your home is a haven of solitude when you need to rest and recharge",
  },
];

export default function VillasGallery() {
  const [activeIndex, setActiveIndex] = useState(0);
  const activeItem = galleryItems[activeIndex];
  const ActiveIcon = activeItem.icon;

  const goToPrevious = () => {
    setActiveIndex((current) => (current - 1 + galleryItems.length) % galleryItems.length);
  };

  const goToNext = () => {
    setActiveIndex((current) => (current + 1) % galleryItems.length);
  };

  return (
    <section className="villas-gallery-section" id="villas-gallery">
      <div className="container">
        <div className="villas-gallery-layout">
          <motion.div
            className="villas-gallery-copy"
            initial={{ opacity: 0, y: 28 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.6 }}
            transition={{ duration: 0.35 }}
          >
            <span className="villas-gallery-kicker">Gallery</span>
            <h2>Life Around Vessella Villas</h2>
            <p>
              A curated preview of community moments, active amenities, and
              private villa spaces designed around comfort, celebration, and calm.
            </p>

            <div className="villas-gallery-slider" aria-label="Gallery slider controls">
              <div className="villas-gallery-slider-controls">
                <button type="button" onClick={goToPrevious} aria-label="Previous gallery image">
                  <ArrowLeft size={18} />
                </button>
                <button type="button" onClick={goToNext} aria-label="Next gallery image">
                  <ArrowRight size={18} />
                </button>
              </div>

              <div className="villas-gallery-slider-dots">
                {galleryItems.map((item, index) => (
                  <button
                    type="button"
                    key={item.title}
                    className={index === activeIndex ? "is-active" : ""}
                    onClick={() => setActiveIndex(index)}
                    aria-label={`Show ${item.title}`}
                  >
                    <span>{String(index + 1).padStart(2, "0")}</span>
                  </button>
                ))}
              </div>
            </div>
          </motion.div>

          <div className="villas-gallery-feature">
            <AnimatePresence mode="wait">
              <motion.div
                className="villas-gallery-feature-media"
                key={activeItem.title}
                initial={{ opacity: 0, scale: 1.02 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.98 }}
                transition={{ duration: 0.2 }}
              >
                <img src={activeItem.image} alt={activeItem.title} loading="lazy" />
              </motion.div>
            </AnimatePresence>

            <div className="villas-gallery-feature-card">
              <span className="villas-gallery-feature-icon" aria-hidden="true">
                <ActiveIcon size={22} />
              </span>
              <div>
                <span className="villas-gallery-feature-count">
                  {String(activeIndex + 1).padStart(2, "0")} / {String(galleryItems.length).padStart(2, "0")}
                </span>
                <h3>{activeItem.title}</h3>
                <p>{activeItem.description}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
